import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../lib/apiClient";
import { useAuth } from "./useAuth";
import { useToast } from "./useToast";

// useHiddenProperties — the signed-in user's "not interested" set (#3074).
// Hidden properties drop off the map and list client-side (the rows are
// already loaded); the Leads table and bell exclude them server-side.
// Toggling is optimistic and reverts on failure.
export function useHiddenProperties() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const userId = user?.id;

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setHiddenIds(new Set());
      return;
    }
    apiClient
      .get<{ data: string[] }>("/hidden")
      .then(({ data }) => !cancelled && setHiddenIds(new Set(data.data || [])))
      .catch(() => {
        // Not fatal: the list just shows everything until the next load.
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const toggle = useCallback(
    async (propertyId: string) => {
      if (!userId) return;
      const wasHidden = hiddenIds.has(propertyId);
      const flip = (hide: boolean) =>
        setHiddenIds((prev) => {
          const next = new Set(prev);
          if (hide) next.add(propertyId);
          else next.delete(propertyId);
          return next;
        });
      flip(!wasHidden);
      try {
        if (wasHidden) await apiClient.delete(`/hidden/${propertyId}`);
        else await apiClient.post(`/hidden/${propertyId}`);
      } catch {
        flip(wasHidden);
        toast(wasHidden ? "Couldn't unhide that property." : "Couldn't hide that property.", "error");
      }
    },
    [userId, hiddenIds, toast]
  );

  // Bulk hide (#3079): optimistic for all, then one POST each; a failed one
  // is put back and reported once.
  const hideMany = useCallback(
    async (ids: string[]) => {
      if (!userId || ids.length === 0) return;
      setHiddenIds((prev) => new Set([...Array.from(prev), ...ids]));
      const failed: string[] = [];
      for (const id of ids) {
        try {
          await apiClient.post(`/hidden/${id}`);
        } catch {
          failed.push(id);
        }
      }
      if (failed.length) {
        setHiddenIds((prev) => {
          const next = new Set(prev);
          failed.forEach((id) => next.delete(id));
          return next;
        });
        toast(`Couldn't hide ${failed.length} of ${ids.length} properties.`, "error");
      }
    },
    [userId, toast]
  );

  return { hiddenIds, toggle, hideMany };
}
